import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CalendarDays, Plus, Trash2, Save, X, Pencil } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyModule = () => ({ module_number: '', module_code: '', module: '', module_hours: '', date_range: '', obs: '' });
const emptyForm = { bundle_id: '', class_name: '', modality: '', start_date: '', end_date: '', instructor: 'Alexander Justi e Equipe AJ', location: '', modules: [emptyModule()] };

const AdminClassSchedules = () => {
  const [data, setData] = useState({ schedules: [], all_bundles: [], bundles_without_schedule: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/class-schedules`, { withCredentials: true })
      .then(r => setData(r.data)).catch(() => toast.error('Erro ao carregar')).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(emptyForm); setEditing(true); };
  const startEdit = (s) => {
    setForm({
      bundle_id: s.bundle_id, class_name: s.class_name || '', modality: s.modality || '',
      start_date: s.start_date || '', end_date: s.end_date || '', instructor: s.instructor || '', location: s.location || '',
      modules: (s.modules && s.modules.length) ? s.modules : [emptyModule()],
    });
    setEditing(true);
  };

  const setMod = (i, k, v) => { const m = [...form.modules]; m[i] = { ...m[i], [k]: v }; setForm({ ...form, modules: m }); };
  const addMod = () => setForm({ ...form, modules: [...form.modules, emptyModule()] });
  const rmMod = (i) => setForm({ ...form, modules: form.modules.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!form.bundle_id || !form.class_name.trim()) { toast.error('Formação e nome da turma são obrigatórios'); return; }
    const payload = {
      bundle_id: parseInt(form.bundle_id), class_name: form.class_name, modality: form.modality,
      start_date: form.start_date, end_date: form.end_date, instructor: form.instructor, location: form.location,
      modules: form.modules.filter(m => m.module || m.date_range).map((m, i) => ({
        module_number: parseInt(m.module_number) || (i + 1), module_code: m.module_code || '',
        module: m.module || '', module_hours: m.module_hours || '', date_range: m.date_range || '', obs: m.obs || '',
      })),
    };
    try { await axios.post(`${API}/admin/class-schedules`, payload, { withCredentials: true }); toast.success('Cronograma salvo'); setEditing(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir este cronograma?')) return;
    try { await axios.delete(`${API}/admin/class-schedules/${id}`, { withCredentials: true }); toast.success('Excluído'); load(); }
    catch { toast.error('Erro ao excluir'); }
  };

  return (
    <div data-testid="admin-class-schedules">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold text-white flex items-center gap-2"><CalendarDays className="text-red-500" size={20} />Turmas & Cronogramas (Anexo III)</h3>
          <p className="text-gray-400 text-sm">Cadastre o cronograma real de cada formação. Ele entra automaticamente no contrato (Anexo III).</p>
        </div>
        <Button onClick={startNew} className="bg-red-600 hover:bg-red-700" data-testid="new-schedule"><Plus size={16} className="mr-2" />Novo cronograma</Button>
      </div>

      {data.bundles_without_schedule.length > 0 && !editing && (
        <div className="bg-amber-950/30 border border-amber-700/40 rounded-lg p-3 mb-4 text-sm text-amber-200" data-testid="schedules-missing">
          {data.bundles_without_schedule.length} formação(ões) ainda sem cronograma: {data.bundles_without_schedule.map(b => b.bundle_title).join(', ')}
        </div>
      )}

      {editing && (
        <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-4 mb-5 space-y-3" data-testid="schedule-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs">Formação</label>
              <select className="w-full bg-zinc-800 border border-zinc-700 rounded-md h-10 px-2 text-white text-sm" value={form.bundle_id} onChange={e => setForm({ ...form, bundle_id: e.target.value })} data-testid="schedule-bundle">
                <option value="">Selecione...</option>
                {data.all_bundles.map(b => <option key={b.bundle_id} value={b.bundle_id}>{b.title}</option>)}
              </select>
            </div>
            <div><label className="text-gray-400 text-xs">Nome da turma</label><Input value={form.class_name} onChange={e => setForm({ ...form, class_name: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" data-testid="schedule-class-name" /></div>
            <div><label className="text-gray-400 text-xs">Modalidade</label><Input value={form.modality} onChange={e => setForm({ ...form, modality: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" /></div>
            <div><label className="text-gray-400 text-xs">Professor</label><Input value={form.instructor} onChange={e => setForm({ ...form, instructor: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" /></div>
            <div><label className="text-gray-400 text-xs">Início</label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" /></div>
            <div><label className="text-gray-400 text-xs">Término</label><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" /></div>
            <div className="md:col-span-2"><label className="text-gray-400 text-xs">Local</label><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" /></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-gray-300 text-sm font-semibold">Módulos</label>
              <Button size="sm" variant="outline" onClick={addMod} data-testid="add-module"><Plus size={14} className="mr-1" />Adicionar módulo</Button>
            </div>
            <div className="space-y-2">
              {form.modules.map((m, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`module-row-${i}`}>
                  <Input placeholder="Nº" value={m.module_number} onChange={e => setMod(i, 'module_number', e.target.value)} className="col-span-1 bg-zinc-800 border-zinc-700 text-white text-sm" />
                  <Input placeholder="Código" value={m.module_code} onChange={e => setMod(i, 'module_code', e.target.value)} className="col-span-2 bg-zinc-800 border-zinc-700 text-white text-sm" />
                  <Input placeholder="Conteúdo do módulo" value={m.module} onChange={e => setMod(i, 'module', e.target.value)} className="col-span-4 bg-zinc-800 border-zinc-700 text-white text-sm" />
                  <Input placeholder="Datas (ex: 01/09 a 15/09)" value={m.date_range} onChange={e => setMod(i, 'date_range', e.target.value)} className="col-span-3 bg-zinc-800 border-zinc-700 text-white text-sm" />
                  <Input placeholder="C.H." value={m.module_hours} onChange={e => setMod(i, 'module_hours', e.target.value)} className="col-span-1 bg-zinc-800 border-zinc-700 text-white text-sm" />
                  <button onClick={() => rmMod(i)} className="col-span-1 text-red-400 flex justify-center"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditing(false)}><X size={16} className="mr-2" />Cancelar</Button>
            <Button onClick={save} className="bg-green-600 hover:bg-green-700" data-testid="save-schedule"><Save size={16} className="mr-2" />Salvar</Button>
          </div>
        </div>
      )}

      {loading ? <p className="text-gray-400">Carregando...</p> : (
        <div className="space-y-2">
          {data.schedules.map(s => (
            <div key={s.id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 flex items-center justify-between" data-testid={`schedule-row-${s.bundle_id}`}>
              <div>
                <div className="text-white font-medium">{s.bundle_title} — {s.class_name}</div>
                <div className="text-gray-500 text-xs">{s.modality} · {s.start_date} a {s.end_date} · {(s.modules || []).length} módulo(s){s.has_legacy_schedule ? ' · cronograma detalhado por dia' : ''}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => startEdit(s)}><Pencil size={14} /></Button>
                <Button size="sm" variant="outline" className="text-red-400 border-red-900/50" onClick={() => remove(s.id)}><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminClassSchedules;
